import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Download, 
  TrendingUp, 
  ArrowRight,
  Calendar,
  ChevronDown,
  FileSpreadsheet,
  FileText,
  FileJson
} from 'lucide-react';
import { toast } from 'sonner';
import { orgApi } from '../utils/apiV2';

// Format currency
const formatCurrency = (amount, currency = 'USD') => {
  const symbols = { USD: '$', AED: 'AED ', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || '$';
  return `${symbol}${Math.abs(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Format date for display in header
const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

const ProfitLossTab = ({ onNavigate }) => {
  // Date range - default to current year
  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      startDate: `${now.getFullYear()}-01-01`,
      endDate: `${now.getFullYear()}-12-31`
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  
  // Get currency from org
  const orgCountry = localStorage.getItem('orgCountry') || 'USA';
  const currency = orgCountry === 'UAE' ? 'AED' : 'USD';
  
  // Fetch all financial data using correct financial endpoints
  const { data: financialData, isLoading, error } = useSWR(
    ['pnl-data', dateRange.startDate, dateRange.endDate],
    async () => {
      const [bookingsRes, expensesRes] = await Promise.all([
        orgApi.getFinancialBookings({ startDate: dateRange.startDate, endDate: dateRange.endDate }),
        orgApi.getExpenses({ startDate: dateRange.startDate, endDate: dateRange.endDate })
      ]);
      return {
        bookings: bookingsRes?.data?.bookings || [],
        summary: bookingsRes?.data?.summary || null,
        expenses: expensesRes?.expenses || []
      };
    },
    { revalidateOnFocus: true }
  );
  
  const bookings = financialData?.bookings || [];
  const expenses = financialData?.expenses || [];
  const backendSummary = financialData?.summary || null;
  
  // Calculate totals - use backend summary if available (single source of truth)
  const totals = useMemo(() => {
    // Backend provides correct pre-calculated values
    if (backendSummary) {
      const expenseTotal = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      const revenue = backendSummary.totalOwnerRevenue || backendSummary.totalRevenue || 0;
      const netProfit = revenue - expenseTotal;
      const profitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
      const expensePercent = revenue > 0 ? (expenseTotal / revenue) * 100 : 0;
      
      // Sum fees from individual bookings
      const stripeFee = backendSummary.totalStripeFees || bookings.reduce((sum, b) => sum + (b.stripeFee || 0), 0);
      const platformFee = backendSummary.totalPlatformFees || bookings.reduce((sum, b) => sum + (b.platformFee || 0), 0);
      const serviceFee = bookings.reduce((sum, b) => sum + (b.serviceFee || 0), 0);
      const totalFees = stripeFee + platformFee + serviceFee;
      const feePercent = revenue > 0 ? (totalFees / revenue) * 100 : 0;
      
      // Calculate subtotal and tax from bookings
      const subtotal = bookings.reduce((sum, b) => sum + (b.subtotal || b.basePrice || 0), 0);
      const tax = bookings.reduce((sum, b) => sum + (b.salesTax || 0), 0);
      
      return { 
        revenue, subtotal, tax, serviceFee, expenseTotal, netProfit, 
        profitMargin, expensePercent, stripeFee, platformFee, totalFees, feePercent,
        bookingCount: bookings.filter(b => b.paymentStatus === 'paid').length, 
        expenseCount: expenses.length
      };
    }
    
    // Fallback: calculate from booking data (now has ownerRevenue from backend)
    let subtotal = 0;
    let tax = 0;
    let serviceFee = 0;
    let stripeFee = 0;
    let platformFee = 0;
    let revenue = 0;
    
    bookings.forEach(b => {
      if (b.paymentStatus !== 'paid') return;
      
      subtotal += b.subtotal || b.basePrice || 0;
      tax += b.salesTax || 0;
      serviceFee += b.serviceFee || 0;
      stripeFee += b.stripeFee || 0;
      platformFee += b.platformFee || 0;
      // Use ownerRevenue (calculated by backend) instead of totalAmount
      revenue += b.ownerRevenue || 0;
    });
    
    const expenseTotal = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    const totalFees = stripeFee + platformFee + serviceFee;
    const netProfit = revenue - expenseTotal;
    const profitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
    const expensePercent = revenue > 0 ? (expenseTotal / revenue) * 100 : 0;
    const feePercent = revenue > 0 ? (totalFees / revenue) * 100 : 0;
    
    return { 
      revenue, subtotal, tax, serviceFee, expenseTotal, netProfit, 
      profitMargin, expensePercent, stripeFee, platformFee, totalFees, feePercent,
      bookingCount: bookings.filter(b => b.paymentStatus === 'paid').length, 
      expenseCount: expenses.length
    };
  }, [bookings, expenses, backendSummary]);
  
  // Recent items (last 5)
  const recentBookings = bookings.filter(b => b.paymentStatus === 'paid').slice(0, 5);
  const recentExpenses = expenses.slice(0, 5);
  
  // Export functions
  const exportCSV = () => {
    const headers = ['Category', 'Amount'];
    const rows = [
      ['Total Revenue', totals.revenue],
      ['Subtotal', totals.subtotal],
      ['Tax', totals.tax],
      ['Service Fee', totals.serviceFee],
      ['Total Expenses', totals.expenseTotal],
      ['Stripe Fees', totals.stripeFee],
      ['Platform Fees', totals.platformFee],
      ['Net Profit', totals.netProfit],
      ['Profit Margin %', totals.profitMargin.toFixed(1)]
    ];
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pnl_summary_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    toast.success('P&L exported as CSV!');
  };
  
  const exportJSON = () => {
    const exportData = {
      period: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      summary: {
        revenue: totals.revenue,
        subtotal: totals.subtotal,
        tax: totals.tax,
        serviceFee: totals.serviceFee,
        expenses: totals.expenseTotal,
        stripeFees: totals.stripeFee,
        platformFees: totals.platformFee,
        netProfit: totals.netProfit,
        profitMargin: totals.profitMargin
      },
      bookingCount: totals.bookingCount,
      expenseCount: totals.expenseCount
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pnl_summary_${dateRange.startDate}_to_${dateRange.endDate}.json`;
    a.click();
    toast.success('P&L exported as JSON!');
  };
  
  const exportPDF = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Profit & Loss Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 600px; margin: 0 auto; }
          h1 { color: #1a1a1a; border-bottom: 2px solid #10b981; padding-bottom: 10px; }
          .period { color: #6b7280; margin-bottom: 20px; }
          .section { background: #f9fafb; border-radius: 8px; padding: 20px; margin: 15px 0; }
          .section h3 { margin: 0 0 15px 0; color: #374151; font-size: 14px; text-transform: uppercase; }
          .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
          .row:last-child { border-bottom: none; }
          .label { color: #6b7280; }
          .value { font-weight: 600; }
          .value.positive { color: #10b981; }
          .value.negative { color: #ef4444; }
          .highlight { background: #10b981; color: white; padding: 20px; border-radius: 8px; text-align: center; margin: 20px 0; }
          .highlight .amount { font-size: 32px; font-weight: bold; }
          .highlight .label { color: rgba(255,255,255,0.8); font-size: 14px; }
        </style>
      </head>
      <body>
        <h1>Profit & Loss Report</h1>
        <p class="period">${dateRange.startDate} to ${dateRange.endDate}</p>
        
        <div class="highlight">
          <div class="amount">${formatCurrency(totals.netProfit, currency)}</div>
          <div class="label">Net Profit</div>
        </div>
        
        <div class="section">
          <h3>Revenue</h3>
          <div class="row"><span class="label">Total Revenue</span><span class="value positive">${formatCurrency(totals.revenue, currency)}</span></div>
          <div class="row"><span class="label">Subtotal</span><span class="value">${formatCurrency(totals.subtotal, currency)}</span></div>
          <div class="row"><span class="label">Tax Collected</span><span class="value">${formatCurrency(totals.tax, currency)}</span></div>
        </div>
        
        <div class="section">
          <h3>Processing Fees</h3>
          <div class="row"><span class="label">Stripe Fees</span><span class="value negative">${formatCurrency(totals.stripeFee, currency)}</span></div>
          <div class="row"><span class="label">Platform Fees</span><span class="value negative">${formatCurrency(totals.platformFee, currency)}</span></div>
          <div class="row"><span class="label">Service Fees</span><span class="value">${formatCurrency(totals.serviceFee, currency)}</span></div>
        </div>
        
        <div class="section">
          <h3>Expenses</h3>
          <div class="row"><span class="label">Total Expenses</span><span class="value negative">${formatCurrency(totals.expenseTotal, currency)}</span></div>
        </div>
        
        <div class="section">
          <h3>Summary</h3>
          <div class="row"><span class="label">Profit Margin</span><span class="value">${totals.profitMargin.toFixed(1)}%</span></div>
          <div class="row"><span class="label">Bookings</span><span class="value">${totals.bookingCount}</span></div>
          <div class="row"><span class="label">Expense Items</span><span class="value">${totals.expenseCount}</span></div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    toast.success('Opening print dialog for PDF...');
  };
  
  // Error handling
  if (error) {
    toast.error('Failed to load financial data');
  }
  
  if (isLoading) {
    return (
      <TabPageLayout title="Profit & Loss" subtitle="Your financial overview">
        <PageLoader />
      </TabPageLayout>
    );
  }
  
  const isProfitable = totals.netProfit >= 0;
  
  // Calculate bar widths for the stacked revenue bar
  const total = totals.subtotal + totals.tax + totals.expenseTotal;
  const subtotalWidth = total > 0 ? (totals.subtotal / total) * 100 : 0;
  const taxWidth = total > 0 ? (totals.tax / total) * 100 : 0;
  const expenseWidth = total > 0 ? (totals.expenseTotal / total) * 100 : 0;
  
  // Fee bar widths (proportional to revenue)
  const feeBarWidth = totals.feePercent;
  const platformFeeWidth = totals.totalFees > 0 ? (totals.platformFee / totals.totalFees) * feeBarWidth : 0;
  const stripeFeeWidth = totals.totalFees > 0 ? (totals.stripeFee / totals.totalFees) * feeBarWidth : 0;
  const serviceFeeWidth = totals.totalFees > 0 ? (totals.serviceFee / totals.totalFees) * feeBarWidth : 0;
  
  return (
    <TabPageLayout 
      title="Profit & Loss" 
      subtitle="Your financial overview"
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 h-10">
              <Download className="w-4 h-4" />
              Export
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportCSV} className="gap-2 cursor-pointer">
              <FileSpreadsheet className="w-4 h-4" />
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportPDF} className="gap-2 cursor-pointer">
              <FileText className="w-4 h-4" />
              Export as PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportJSON} className="gap-2 cursor-pointer">
              <FileJson className="w-4 h-4" />
              Export as JSON
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <div className="space-y-6">
        {/* Date Range Picker */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="border-none bg-transparent text-sm text-gray-900 focus:outline-none w-28"
              aria-label="Start date"
            />
          </div>
          <span className="text-gray-500 text-sm">to</span>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="border-none bg-transparent text-sm text-gray-900 focus:outline-none w-28"
              aria-label="End date"
            />
          </div>
        </div>
        
        {/* P&L Visual Card - Dark Theme */}
        <div className="bg-gray-900 rounded-xl p-6 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
          {/* Header with date range and Net Profit in top right */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-400 text-sm">
              {formatDateRange(dateRange.startDate, dateRange.endDate)} Performance
            </p>
            <div className={`px-4 py-2 rounded-full ${isProfitable ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              <span className={`text-lg font-medium ${isProfitable ? 'text-green-400' : 'text-red-400'}`}>
                {isProfitable ? '' : '-'}{formatCurrency(Math.abs(totals.netProfit), currency)}
              </span>
              <span className={`text-sm ml-2 ${isProfitable ? 'text-green-400/70' : 'text-red-400/70'}`}>
                Net {isProfitable ? 'Profit' : 'Loss'}
              </span>
            </div>
          </div>
          
          {/* Revenue/Expenses Bar */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-400">Revenue: {formatCurrency(totals.revenue, currency)}</span>
              <span className="text-red-400">Expenses: {formatCurrency(totals.expenseTotal, currency)}</span>
            </div>
            
            {/* Stacked Bar */}
            <div className="h-5 bg-gray-700 rounded-full overflow-hidden flex">
              <div 
                className="h-full bg-green-500 transition-all duration-500"
                style={{ width: `${subtotalWidth}%` }}
                title={`Subtotal: ${formatCurrency(totals.subtotal, currency)}`}
              />
              <div 
                className="h-full bg-amber-500 transition-all duration-500"
                style={{ width: `${taxWidth}%` }}
                title={`Tax: ${formatCurrency(totals.tax, currency)}`}
              />
              <div 
                className="h-full bg-red-500 transition-all duration-500"
                style={{ width: `${expenseWidth}%` }}
                title={`Expenses: ${formatCurrency(totals.expenseTotal, currency)}`}
              />
            </div>
            
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>Net: {formatCurrency(totals.netProfit, currency)}</span>
              <span>{totals.expensePercent.toFixed(1)}% spent</span>
            </div>
          </div>
          
          {/* Processing Fees Bar */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-300">Processing Fees:</span>
              <span className="text-gray-300">{formatCurrency(totals.totalFees, currency)}</span>
            </div>
            
            <div className="h-5 bg-gray-700 rounded-full overflow-hidden flex">
              <div 
                className="h-full bg-blue-500 transition-all duration-500"
                style={{ width: `${platformFeeWidth}%` }}
                title={`BookingFlows: ${formatCurrency(totals.platformFee, currency)}`}
              />
              <div 
                className="h-full bg-purple-500 transition-all duration-500"
                style={{ width: `${stripeFeeWidth}%` }}
                title={`Stripe: ${formatCurrency(totals.stripeFee, currency)}`}
              />
              <div 
                className="h-full bg-yellow-500 transition-all duration-500"
                style={{ width: `${serviceFeeWidth}%` }}
                title={`Service Fee: ${formatCurrency(totals.serviceFee, currency)}`}
              />
            </div>
            
            <div className="flex items-center text-xs">
              <div className="flex items-center gap-4">
                <span className="text-blue-400">BookingFlows: {formatCurrency(totals.platformFee, currency)}</span>
                <span className="text-purple-400">Stripe: {formatCurrency(totals.stripeFee, currency)}</span>
                {totals.serviceFee > 0 && (
                  <span className="text-yellow-400">Service: {formatCurrency(totals.serviceFee, currency)}</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Bottom Stats */}
          <div className="flex flex-wrap items-center justify-between pt-4 border-t border-gray-700 gap-4">
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-400">Subtotal: <span className="text-white">{formatCurrency(totals.subtotal, currency)}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <span className="text-gray-400">Tax: <span className="text-white">{formatCurrency(totals.tax, currency)}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-gray-400">Expenses: <span className="text-white">{formatCurrency(totals.expenseTotal, currency)}</span></span>
              </div>
            </div>
            <div className="text-sm text-gray-400">
              {totals.bookingCount} bookings · {totals.expenseCount} expenses
            </div>
          </div>
        </div>
        
        {/* Recent Revenue & Expenses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Recent Revenue */}
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Recent Revenue</h3>
              <button 
                onClick={() => onNavigate && onNavigate('revenue')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                See All <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {recentBookings.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 text-center">No revenue yet</p>
              ) : (
                recentBookings.map((booking) => (
                  <div key={booking.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                    <div>
                      <p className="text-sm text-gray-900">{booking.customerName || booking.customer_name || 'Customer'}</p>
                      <p className="text-xs text-gray-500">{formatDate(booking.bookingDate || booking.booking_date)}</p>
                    </div>
                    <span className="text-sm text-green-600">
                      +{formatCurrency(booking.totalAmount || booking.total_amount, currency)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Recent Expenses */}
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Recent Expenses</h3>
              <button 
                onClick={() => onNavigate && onNavigate('expenses')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                Add Expense <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {recentExpenses.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 text-center">No expenses yet</p>
              ) : (
                recentExpenses.map((expense) => (
                  <div key={expense.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                    <div>
                      <p className="text-sm text-gray-900">{expense.title || expense.description || 'Expense'}</p>
                      <p className="text-xs text-gray-500">{formatDate(expense.date || expense.expense_date)}</p>
                    </div>
                    <span className="text-sm text-red-600">
                      -{formatCurrency(expense.amount, currency)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </TabPageLayout>
  );
};

export default ProfitLossTab;
